/**
 * SetupWizard — the first-run journey on the hub dashboard, in two acts.
 *
 * Act 1, `variant="gate"` (the default): the mandatory onboarding wizard. The
 * hub (LaunchDashboard) holds its lifecycle pipeline back behind this card
 * while onboarding runs. There is no dismiss and no skip of the card in
 * this mode; that is what makes it mandatory. Step 2 connects the repo INLINE
 * (picker + plan-limit handling right here) instead of deep-linking away to
 * Settings, which used to strand the user at the top of a page about the step
 * they had already done. Step 3 — "Choose what SystemDox documents"
 * (guided-repo-setup Phase C) — assigns the five capability roles, with
 * one-click same-repo-for-all as the default; it sequences via pendingRepo
 * (the connected repo is held back from the hub until the roles resolve), so
 * the hub's own gate still opens on a connected repo alone and the step can
 * never trap anyone.
 *
 * Act 2, `variant="journey"`: once the dashboard unlocks, the same card
 * renders ABOVE the dashboard and walks the user through the product's value
 * loop once — capture a first requirement. It ticks off live state,
 * auto-dismisses (via onDismiss) when the loop completes, and can be hidden
 * explicitly; the dismissal latch itself lives in LaunchDashboard, never here.
 *
 * The hub only ever shows the gate to a workspace owner/admin whose repo read
 * succeeded and came back empty, and it latches completion — a non-admin
 * member, a failed read, or a prior completion all fall through to the
 * dashboard, so the un-dismissable card can never trap a legitimate user. See
 * LaunchDashboard for that gate.
 *
 * Every step's completion derives from live API state: GitHub installations,
 * repos and requirement count (passed in), plus member count (fetched here).
 * CHECKLIST_ACTIONS is the shared SSOT for the step
 * labels and routes, so the wizard, the page empty states and the deep-links
 * cannot drift apart.
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  ArrowRight, Check, CheckCircle2, GitBranch, Globe, Lock, Rocket, Terminal, X,
} from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { useTenant } from '@/contexts/TenantContext';
import { tenantApi } from '@/services/tenantApi';
import { docsApi } from '@/services/docsApi';
import { githubApi, RepoLimitError, type PlanUsage, type Repo } from '@/services/githubApi';
import {
  specsApi, CAPABILITY_KEYS, type Capabilities, type CapabilityKey,
} from '@/services/specsApi';
import { CHECKLIST_ACTIONS, CtaLink, type ChecklistActionKey } from './SetupChecklist';
import { cn } from '@/lib/utils';

// The required steps sequence the gate; the journey steps guide the first
// pass through the value loop; invite never gates anything. Splitting the
// keys out (rather than flags per row) lets the guard test pin each
// partition to an exact literal, so moving a step across a line is a visible
// edit, not a silent one. NOTE: the hub's gate itself still opens on a
// connected repo alone (LaunchDashboard requiredComplete) — the roles step
// sequences INSIDE the wizard via pendingRepo, so a reload or an API failure
// can never trap anyone behind it.
const REQUIRED_KEYS: ChecklistActionKey[] = ['connectGithub', 'indexRepo', 'roles'];
const JOURNEY_KEYS: ChecklistActionKey[] = ['capture'];
const OPTIONAL_KEYS: ChecklistActionKey[] = ['invite'];

interface WizardSignals {
  githubConnected: boolean;
  repoConnected: boolean;
  rolesDecided: boolean;
  hasRequirement: boolean;
  hasTeammate: boolean;
}

/** Per-step copy plus how each derives done/locked from the live signals. */
const STEP_CONFIG: Record<ChecklistActionKey, {
  description: string;
  cta: string;
  state: (s: WizardSignals) => { done: boolean; locked: boolean };
}> = {
  connectGithub: {
    description: 'Install the GitHub App and choose the repos SystemDox can see.',
    cta: 'Connect',
    state: (s) => ({ done: s.githubConnected, locked: false }),
  },
  indexRepo: {
    // Done the moment a repo is connected (its sync is kicked off then) — NOT
    // keyed off doc count. A code-only repo indexes to zero docs, and gating on
    // that would trap the user behind this un-dismissable wizard forever.
    description: 'Pick the repository SystemDox will sync and index.',
    cta: 'Index',
    state: (s) => ({ done: s.repoConnected, locked: !s.githubConnected }),
  },
  roles: {
    // Satisfied when every capability has left 'unset' — any mix of enabled
    // and disabled counts as decided (guided-repo-setup Phase C).
    description: 'Point requirements, design, specs, checks and component docs at your repos — or use one repo for everything.',
    cta: 'Choose',
    state: (s) => ({ done: s.rolesDecided, locked: !s.repoConnected }),
  },
  capture: {
    description: 'Type or dictate a requirement — SystemDox drafts the spec for you.',
    cta: 'Capture',
    state: (s) => ({ done: s.hasRequirement, locked: !s.repoConnected }),
  },
  invite: {
    description: 'Bring your team into the workspace.',
    cta: 'Invite',
    state: (s) => ({ done: s.hasTeammate, locked: false }),
  },
};

interface WizardStep {
  key: ChecklistActionKey;
  title: string;
  description: string;
  cta: string;
  done: boolean;
  locked: boolean;
}

function buildStep(key: ChecklistActionKey, signals: WizardSignals): WizardStep {
  const cfg = STEP_CONFIG[key];
  const { done, locked } = cfg.state(signals);
  return { key, title: CHECKLIST_ACTIONS[key].label, description: cfg.description, cta: cfg.cta, done, locked };
}

/**
 * Inline repo connect — the whole point of the wizard's step 2. Lists the
 * repos the GitHub App can see and connects one right here (kicking off its
 * first sync), instead of bouncing the user to Settings and hoping they scroll
 * to the right section. Plan limits are enforced the same way Settings does
 * it: intercept up front via at_limit, and still catch the backend's
 * RepoLimitError if a race slips through.
 */
function InlineRepoPicker({
  repos,
  planUsage,
  onConnected,
  resolveTo,
}: {
  repos: Repo[];
  planUsage: PlanUsage | null;
  onConnected: (fullName: string) => void;
  resolveTo: (path: string) => string;
}) {
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const available = repos.filter((r) => !r.connected);
  const filtered = search
    ? available.filter((r) => r.full_name.toLowerCase().includes(search.toLowerCase()))
    : available;
  const atLimit = planUsage?.at_limit ?? false;

  async function connect(repo: Repo) {
    setBusy(repo.full_name);
    setError(null);
    try {
      await githubApi.connectRepo(repo.owner, repo.repo);
      // Kick off the first index; the dashboard picks the counts up on its
      // next load, so there is nothing to poll for here.
      void docsApi.syncRepo(repo.owner, repo.repo).catch(() => {});
      onConnected(repo.full_name);
    } catch (e) {
      if (e instanceof RepoLimitError) {
        setError(
          `Your ${e.plan} plan allows ${e.limit} connected ${e.limit === 1 ? 'repository' : 'repositories'}.`,
        );
      } else {
        setError('Could not connect the repository — try again, or manage it from Settings.');
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700">
      {available.length === 0 ? (
        <p className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400">
          The GitHub App can’t see any repositories yet. Grant it access to the repo you want
          documented, then come back here.
        </p>
      ) : (
        <>
          {atLimit && (
            <p className="border-b border-slate-200 px-3 py-2 text-xs text-amber-700 dark:border-slate-700 dark:text-amber-400">
              Your plan’s repository limit is reached ({planUsage?.connected_count} of {planUsage?.repo_limit}).
            </p>
          )}
          {error && (
            <p className="border-b border-slate-200 px-3 py-2 text-xs text-red-600 dark:border-slate-700 dark:text-red-400">
              {error}
            </p>
          )}
          {available.length > 6 && (
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search repositories…"
              className="w-full border-b border-slate-200 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-slate-400 dark:border-slate-700"
            />
          )}
          <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
            {filtered.map((repo) => (
              <li key={repo.full_name} className="flex items-center gap-2 px-3 py-2">
                {repo.private ? (
                  <Lock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                ) : (
                  <Globe className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                )}
                <span className="min-w-0 flex-1 truncate text-sm text-slate-700 dark:text-slate-300">
                  {repo.full_name}
                </span>
                <button
                  type="button"
                  onClick={() => connect(repo)}
                  disabled={busy !== null || atLimit}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                >
                  {busy === repo.full_name && <Spinner size="sm" tone="current" />}
                  Connect
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                No repositories match.
              </li>
            )}
          </ul>
        </>
      )}
      <div className="border-t border-slate-100 px-3 py-2 dark:border-slate-800">
        <CtaLink
          to={resolveTo(CHECKLIST_ACTIONS.indexRepo.to)}
          className="text-xs text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Manage repositories in Settings
        </CtaLink>
      </div>
    </div>
  );
}

/** Wizard copy per capability row — keys mirror the /doc-repos API. */
const CAPABILITY_ROWS: { key: CapabilityKey; label: string; hint: string }[] = [
  { key: 'requirements', label: 'Requirements', hint: 'what the system must do' },
  { key: 'design', label: 'Design', hint: 'ADRs and how it’s built' },
  { key: 'specs', label: 'Specs', hint: 'spec-driven changes' },
  { key: 'fitness', label: 'Fitness tests', hint: 'architecture checks (Checks)' },
  { key: 'components', label: 'Component docs', hint: 'docs/ in each component repo' },
];

const SKIP = '__skip__';

/**
 * Step 3 — "Choose what SystemDox documents" (guided-repo-setup Phase C).
 * The one-click default assigns every capability to the just-connected repo,
 * so the free tier finishes setup in a single decision; the expansion assigns
 * per role across the CONNECTED repos, with a per-row "Skip — we don't use
 * this" writing the deliberate 'disabled' opt-out. Writes go through the
 * capabilities API so Settings ▸ Repository roles is populated from day one.
 * The escape link exists so a failing write can never trap anyone here — the
 * hub's gate opens on the connected repo regardless (see REQUIRED_KEYS note).
 */
function RolesStep({
  primaryRepo,
  repoOptions,
  onDone,
  onDefer,
}: {
  /** The repo connected in step 2 — the one-click "for everything" target. */
  primaryRepo: string;
  /** Connected repos offered by the per-role selectors. */
  repoOptions: string[];
  onDone: () => void;
  onDefer: () => void;
}) {
  const [split, setSplit] = useState(false);
  const [choices, setChoices] = useState<Record<CapabilityKey, string>>(() => {
    const initial = {} as Record<CapabilityKey, string>;
    for (const { key } of CAPABILITY_ROWS) initial[key] = primaryRepo;
    return initial;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    const capabilities = {} as Partial<Capabilities>;
    for (const { key } of CAPABILITY_ROWS) {
      const choice = split ? choices[key] : primaryRepo;
      capabilities[key] =
        choice === SKIP
          ? { status: 'disabled' }
          : key === 'components'
            ? { status: 'enabled', repos: [choice] }
            : { status: 'enabled', repo: choice };
    }
    try {
      await specsApi.saveCapabilities(capabilities);
      onDone();
    } catch (e) {
      const data = (e as { response?: { data?: { detail?: string; message?: string } } })?.response?.data;
      setError(data?.detail || data?.message || 'Could not save the roles — try again, or configure them later in Settings.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700">
      <div className="space-y-2 px-3 py-3">
        <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="radio"
            name="roles-mode"
            checked={!split}
            onChange={() => setSplit(false)}
            className="mt-0.5"
          />
          <span>
            Use <strong className="text-slate-900 dark:text-slate-100">{primaryRepo}</strong> for
            everything <span className="text-slate-400">(recommended)</span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="radio"
            name="roles-mode"
            checked={split}
            onChange={() => setSplit(true)}
            className="mt-0.5"
          />
          <span>Assign per role — or skip the ones you don’t use</span>
        </label>
      </div>

      {split && (
        <ul className="divide-y divide-slate-100 border-t border-slate-200 dark:divide-slate-800 dark:border-slate-700">
          {CAPABILITY_ROWS.map(({ key, label, hint }) => (
            <li key={key} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-700 dark:text-slate-300">{label}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500">{hint}</p>
              </div>
              <select
                value={choices[key]}
                onChange={(e) => setChoices((prev) => ({ ...prev, [key]: e.target.value }))}
                className={cn(
                  'max-w-[14rem] shrink-0 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900',
                  choices[key] === SKIP && 'text-slate-400',
                )}
              >
                {repoOptions.map((repo) => (
                  <option key={repo} value={repo}>{repo}</option>
                ))}
                <option value={SKIP}>Skip — we don’t use this</option>
              </select>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="border-t border-slate-200 px-3 py-2 text-xs text-red-600 dark:border-slate-700 dark:text-red-400">
          {error}
        </p>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-3 py-2 dark:border-slate-800">
        <button
          type="button"
          onClick={onDefer}
          className="text-xs text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Skip for now — configure later in Settings
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving && <Spinner size="sm" tone="current" />}
          Save & continue
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** Ring/marker + CTA for one step; also the secondary MCP link on Connect.
 *  `children` (the inline picker) replaces the CTA while the step is active. */
function StepRow({
  step,
  index,
  resolveTo,
  showMcp = false,
  children,
}: {
  step: WizardStep;
  /** 1-based number for the sequenced steps; omitted for the optional ones. */
  index?: number;
  resolveTo: (path: string) => string;
  showMcp?: boolean;
  children?: ReactNode;
}) {
  const action = CHECKLIST_ACTIONS[step.key];
  const state = step.done ? 'done' : step.locked ? 'locked' : 'active';

  return (
    <li className={cn('flex items-start gap-3 px-5 py-4', state === 'locked' && 'opacity-60')}>
      <div className="mt-0.5 shrink-0">
        {state === 'done' ? (
          <CheckCircle2 className="h-6 w-6 text-emerald-500" />
        ) : state === 'locked' ? (
          <div className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 dark:border-slate-700">
            <Lock className="h-3 w-3 text-slate-400" />
          </div>
        ) : (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-xs font-bold text-white">
            {index ?? <span className="h-2 w-2 rounded-full bg-white" />}
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <span
          className={cn(
            'text-sm font-medium text-slate-900 dark:text-slate-100',
            step.done && 'text-slate-400 line-through dark:text-slate-500',
          )}
        >
          {step.title}
        </span>
        <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{step.description}</p>
        {showMcp && !step.done && (
          <CtaLink
            to={resolveTo('/settings/connect')}
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <Terminal className="h-3.5 w-3.5" />
            Or connect your AI via MCP first
          </CtaLink>
        )}
        {state === 'active' && children}
      </div>

      {state === 'active' && !children && (
        <CtaLink
          to={resolveTo(action.to)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
        >
          {step.key === 'connectGithub' && <GitBranch className="h-4 w-4" />}
          {step.cta}
          <ArrowRight className="h-4 w-4" />
        </CtaLink>
      )}
      {state === 'done' && (
        <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          <Check className="h-3.5 w-3.5" /> Done
        </span>
      )}
    </li>
  );
}

export function SetupWizard({
  githubConnected,
  repoConnected,
  hasRequirement,
  repos,
  planUsage,
  onRepoConnected,
  resolveTo,
  variant = 'gate',
  onDismiss,
}: {
  githubConnected: boolean;
  repoConnected: boolean;
  /** At least one requirement spec exists — ticks the capture step. */
  hasRequirement: boolean;
  /** Every repo the GitHub App can see (connected or not) — feeds the picker. */
  repos: Repo[];
  planUsage: PlanUsage | null;
  /** The hub owns the repo list; connecting inline reports back through this. */
  onRepoConnected: (fullName: string) => void;
  /** Maps a console-relative path to the hub's same-origin absolute URL. */
  resolveTo: (path: string) => string;
  /** 'gate' blocks the dashboard until required steps land; 'journey' rides
   *  above the unlocked dashboard guiding the first capture. */
  variant?: 'gate' | 'journey';
  /** Journey-only: latch the card away (explicitly, or when the loop is done).
   *  The latch itself is LaunchDashboard's — the gate variant must stay
   *  un-dismissable, so no storage and no dismiss control exist here. */
  onDismiss?: () => void;
}) {
  const { tenant } = useTenant();
  const isJourney = variant === 'journey';
  const [hasTeammate, setHasTeammate] = useState(false);
  const [rolesDecided, setRolesDecided] = useState(false);
  const [signalsLoaded, setSignalsLoaded] = useState(false);

  // The repo connected inline THIS session, held back from the hub until the
  // roles step resolves — that sequencing is what puts "Choose what SystemDox
  // documents" between the connect and the journey. The hub's own gate keys
  // off its repos read, so a reload mid-step simply opens the dashboard:
  // the sequencing can never trap anyone.
  const [pendingRepo, setPendingRepo] = useState<string | null>(null);

  // Journey/optional-step ticks. Best-effort: a failed fetch just leaves the
  // step open, and none of these gate the dashboard. The gate variant renders
  // without waiting; the journey variant waits for signalsLoaded so an
  // already-finished workspace never flashes a stale card.
  useEffect(() => {
    if (!tenant) return;
    let cancelled = false;
    const members = tenantApi
      .listMembers(tenant.tenantId)
      .then((list) => {
        if (!cancelled) setHasTeammate(list.length > 1);
      })
      .catch(() => {
        if (!cancelled) setHasTeammate(false);
      });
    const roles = specsApi
      .getDocRepos()
      .then((docRepos) => {
        // Decided = every capability has left 'unset' (enabled OR disabled).
        // Pre-capability backends return no capabilities — treat as undecided.
        const caps = docRepos.capabilities;
        if (!cancelled) {
          setRolesDecided(!!caps && CAPABILITY_KEYS.every((key) => caps[key]?.status !== 'unset'));
        }
      })
      .catch(() => {
        if (!cancelled) setRolesDecided(false);
      });
    Promise.allSettled([members, roles]).then(() => {
      if (!cancelled) setSignalsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  // Roles step resolved (saved or deferred): release the held-back repo to
  // the hub, which flips its gate open and brings the journey card in.
  const finishGate = () => {
    if (pendingRepo) {
      onRepoConnected(pendingRepo);
      setPendingRepo(null);
    }
  };

  // The journey exists to be finished: once the first requirement is captured,
  // latch it away for good rather than showing a fully ticked card forever.
  const journeyDone = hasRequirement;
  useEffect(() => {
    if (isJourney && signalsLoaded && journeyDone) onDismiss?.();
  }, [isJourney, signalsLoaded, journeyDone, onDismiss]);

  if (isJourney && (!signalsLoaded || journeyDone)) return null;

  const signals: WizardSignals = {
    githubConnected,
    // A just-connected repo held back for the roles step still counts as
    // connected inside the wizard — step 2 ticks, step 3 unlocks.
    repoConnected: repoConnected || pendingRepo !== null,
    rolesDecided,
    hasRequirement,
    hasTeammate,
  };
  const required = REQUIRED_KEYS.map((key) => buildStep(key, signals));
  const journey = JOURNEY_KEYS.map((key) => buildStep(key, signals));
  const optional = OPTIONAL_KEYS.map((key) => buildStep(key, signals));

  const sequenced = isJourney ? journey : required;
  const sequencedDone = sequenced.filter((s) => s.done).length;

  return (
    <div className="mb-8 overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="border-b border-slate-100 bg-gradient-to-r from-emerald-50/60 to-blue-50/60 p-5 dark:border-slate-800 dark:from-emerald-950/30 dark:to-blue-950/20">
        <div className="flex items-center gap-2">
          <Rocket className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            {isJourney ? 'You’re connected — see SystemDox in action' : 'Set up SystemDox'}
          </h2>
          <span className="ml-auto text-xs font-medium text-slate-500 dark:text-slate-400">
            {sequencedDone} of {sequenced.length} {isJourney ? '' : 'required'}
          </span>
          {isJourney && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Hide guide"
              title="Hide guide"
              className="rounded p-1 text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {isJourney
            ? 'See SystemDox in action: capture your first requirement and let it draft the spec.'
            : 'Finish these steps to unlock your dashboard.'}
        </p>
        <div
          className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
          role="progressbar"
          aria-valuenow={sequencedDone}
          aria-valuemin={0}
          aria-valuemax={sequenced.length}
        >
          <div
            className="h-full rounded-full bg-emerald-500 transition-all"
            style={{ width: `${(sequencedDone / sequenced.length) * 100}%` }}
          />
        </div>
      </div>

      <ol className="divide-y divide-slate-100 dark:divide-slate-800">
        {sequenced.map((step, i) => (
          <StepRow
            key={step.key}
            step={step}
            index={i + 1}
            resolveTo={resolveTo}
            showMcp={step.key === 'connectGithub'}
          >
            {step.key === 'indexRepo' && (
              <InlineRepoPicker
                repos={repos}
                planUsage={planUsage}
                // Held back from the hub until the roles step resolves; the
                // backend connect already happened inside the picker.
                onConnected={setPendingRepo}
                resolveTo={resolveTo}
              />
            )}
            {step.key === 'roles' && (
              <RolesStep
                primaryRepo={pendingRepo ?? repos.find((r) => r.connected)?.full_name ?? ''}
                repoOptions={[...new Set([
                  ...repos.filter((r) => r.connected).map((r) => r.full_name),
                  ...(pendingRepo ? [pendingRepo] : []),
                ])]}
                onDone={() => {
                  setRolesDecided(true);
                  finishGate();
                }}
                onDefer={finishGate}
              />
            )}
          </StepRow>
        ))}
      </ol>

      <div className="border-t border-slate-100 px-5 pt-3 dark:border-slate-800">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
          {isJourney ? 'Optional next steps' : 'Up next'}
        </p>
      </div>
      <ol className="divide-y divide-slate-100 dark:divide-slate-800">
        {(isJourney ? optional : [...journey, ...optional]).map((step) => (
          <StepRow key={step.key} step={step} resolveTo={resolveTo} />
        ))}
      </ol>
    </div>
  );
}
